import { useState, useEffect } from 'react'
import { Button, Input } from '../components/ui'
import { LocationModal } from '../components/locations/LocationModal'
import { useCompany } from '../hooks/useCompany'
// import WifiNetworkManager from '../components/WifiNetworkManager' // Disabled until native app

interface Location {
  id: string
  team_id: string
  name: string
  address?: string
  restrict_to_location_members: boolean
  created_at: number
  geofences?: Array<{
    id: string
    type: 'CIRCLE' | 'POLYGON'
    data: any
  }>
  meta?: {
    coordinates?: { lat: number; lng: number }
    geofences?: any[]
    constraintSets?: string[]
  }
  memberCount?: number
  constraintSets?: string[]
  enforceGeofence?: boolean
}

export default function Locations() {
  const { currentCompany } = useCompany()
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)

  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add'>('view')
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch locations for current team
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchLocations()
  }, [currentTeamId])

  const fetchLocations = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/locations?team_id=${currentTeamId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch locations')
      }

      const data = await response.json()
      setLocations(data.locations || [])
      // Clear selection when locations are refreshed
      setSelectedLocationIds(new Set())
    } catch (err) {
      console.error('Failed to fetch locations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleViewLocation = (location: Location) => {
    setSelectedLocation(location)
    setModalMode('view')
    setIsLocationModalOpen(true)
  }

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location)
    setModalMode('edit')
    setIsLocationModalOpen(true)
  }

  const handleAddLocation = () => {
    setSelectedLocation(null)
    setModalMode('add')
    setIsLocationModalOpen(true)
  }

  const closeModal = () => {
    setIsLocationModalOpen(false)
    setSelectedLocation(null)
    setModalMode('add')
  }

  const handleEditFromView = () => {
    setModalMode('edit')
  }

  const getGeofenceDescription = (location: Location) => {
    if (!location.geofences || location.geofences.length === 0) {
      return 'No geofence'
    }

    if (location.geofences.length === 1) {
      const geofence = location.geofences[0]
      if (geofence.type === 'CIRCLE') {
        const radius = geofence.data?.radius || 100
        return `Circle (${radius}m)`
      } else {
        const points = geofence.data?.points?.length || 0
        return `Polygon (${points} points)`
      }
    } else {
      return `${location.geofences.length} geofences`
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  const handleSaveLocation = async (locationData: any) => {
    try {
      const endpoint = modalMode === 'add' 
        ? `${import.meta.env.VITE_API_BASE_URL}/v1/locations`
        : `${import.meta.env.VITE_API_BASE_URL}/v1/locations/${selectedLocation?.id}`
      
      const method = modalMode === 'add' ? 'POST' : 'PUT'
      
      // Convert our geofence format to the API format
      // For now, we'll only send the first geofence since the API expects a single one
      const firstGeofence = locationData.geofences[0]
      const apiLocationData = {
        name: locationData.name,
        type: 'physical' as const, // Always physical since we're using geofences
        address: locationData.address,
        restrict_to_location_members: !locationData.isAllMembers,
        team_id: currentTeamId,
        enforce_geofence: locationData.enforceGeofence || false,
        geofence: firstGeofence ? {
          type: firstGeofence.type.toLowerCase(),
          data: firstGeofence.type === 'CIRCLE' 
            ? { name: firstGeofence.name, center: firstGeofence.center, radius: firstGeofence.radius }
            : { name: firstGeofence.name, points: firstGeofence.points }
        } : undefined
      }
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiLocationData)
      })
      
      if (!response.ok) {
        throw new Error('Failed to save location')
      }
      
      // Refresh locations list
      await fetchLocations()
      closeModal()
    } catch (err) {
      console.error('Failed to save location:', err)
      alert('Failed to save location. Please try again.')
    }
  }

  const handleSelectLocation = (locationId: string) => {
    const newSelected = new Set(selectedLocationIds)
    if (newSelected.has(locationId)) {
      newSelected.delete(locationId)
    } else {
      newSelected.add(locationId)
    }
    setSelectedLocationIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedLocationIds.size === filteredLocations.length) {
      // Deselect all
      setSelectedLocationIds(new Set())
    } else {
      // Select all filtered locations
      const allIds = new Set(filteredLocations.map(loc => loc.id))
      setSelectedLocationIds(allIds)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedLocationIds.size === 0) return

    const confirmMessage = selectedLocationIds.size === 1 
      ? 'Are you sure you want to delete this location?' 
      : `Are you sure you want to delete ${selectedLocationIds.size} locations?`

    if (!confirm(confirmMessage)) return

    setIsDeleting(true)
    const deletedCount = selectedLocationIds.size
    try {
      // Delete each selected location
      const deletePromises = Array.from(selectedLocationIds).map(async (locationId) => {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/locations/${locationId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )
        if (!response.ok) {
          throw new Error(`Failed to delete location ${locationId}`)
        }
      })

      await Promise.all(deletePromises)
      
      // Clear selection and refresh
      setSelectedLocationIds(new Set())
      await fetchLocations()
      
      alert(`Successfully deleted ${deletedCount} location(s)`)
    } catch (err) {
      console.error('Failed to delete locations:', err)
      alert('Failed to delete some locations. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex h-full bg-white">
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-center">
              <div className="h-8 bg-gray-200 rounded w-32 mb-4 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full bg-white">
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load locations</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button 
                onClick={fetchLocations}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // No company selected state
  if (!currentCompany) {
    return (
      <div className="flex h-full bg-white">
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
              <p className="text-gray-600">Please select a company to view locations.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full bg-white">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
            <p className="text-sm text-gray-600 mt-1">{currentCompany.name}</p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedLocationIds.size > 0 && (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  {selectedLocationIds.size} selected
                </span>
                <Button 
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </Button>
              </div>
            )}
            <Button 
              onClick={handleAddLocation}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Add Location
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex rounded-lg">
            <div className="flex items-center bg-gray-100 rounded-l-lg px-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <Input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="rounded-l-none border-l-0 bg-gray-100 border-gray-100 focus:bg-white focus:border-primary-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {filteredLocations.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No matching locations' : 'No locations yet'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm 
                    ? 'Try adjusting your search terms'
                    : 'Get started by adding your first location'
                  }
                </p>
                {!searchTerm && (
                  <Button 
                    onClick={handleAddLocation}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Add Location
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg mx-4 mb-4">
              {/* Table Header */}
              <div className="bg-gray-50 border-b border-gray-200 rounded-t-lg">
                <div className="grid grid-cols-6 gap-4 px-6 py-3">
                  <div className="text-left col-span-1">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={filteredLocations.length > 0 && selectedLocationIds.size === filteredLocations.length}
                      onChange={handleSelectAll}
                    />
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</div>
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Access</div>
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Geofence</div>
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</div>
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {filteredLocations.map((location) => (
                  <div 
                    key={location.id} 
                    className="grid grid-cols-6 gap-4 px-6 py-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center col-span-1">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={selectedLocationIds.has(location.id)}
                        onChange={() => handleSelectLocation(location.id)}
                      />
                    </div>
                    <div className="flex items-center">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {location.name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        location.restrict_to_location_members 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {location.restrict_to_location_members ? 'Members Only' : 'All Team Members'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="text-sm text-gray-600">
                        {getGeofenceDescription(location)}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="text-sm text-gray-600">
                        {formatDate(location.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleViewLocation(location)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEditLocation(location)}
                        className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location Modal for Add/Edit/View */}
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={closeModal}
        onSave={handleSaveLocation}
        mode={modalMode}
        onEdit={modalMode === 'view' ? handleEditFromView : undefined}
        location={(modalMode === 'edit' || modalMode === 'view') && selectedLocation ? {
          ...selectedLocation,
          address: selectedLocation.address || '',
          coordinates: (() => {
            // Extract coordinates from the first geofence if available
            const firstGeofence = selectedLocation.geofences?.[0]
            if (firstGeofence?.type === 'CIRCLE' && firstGeofence.data?.center) {
              return firstGeofence.data.center
            } else if (firstGeofence?.type === 'POLYGON' && firstGeofence.data?.points?.length > 0) {
              // For polygon, use the centroid or first point
              const points = firstGeofence.data.points
              const avgLat = points.reduce((sum: number, p: any) => sum + p.lat, 0) / points.length
              const avgLng = points.reduce((sum: number, p: any) => sum + p.lng, 0) / points.length
              return { lat: avgLat, lng: avgLng }
            }
            // Default fallback
            return { lat: 40.7128, lng: -74.0060 }
          })(),
          geofences: selectedLocation.geofences?.map((g, index) => ({
            id: g.id,
            type: g.type,
            name: g.data?.name || `Geofence ${index + 1}`,
            center: g.type === 'CIRCLE' ? g.data?.center : undefined,
            radius: g.type === 'CIRCLE' ? (g.data?.radius || 100) : undefined,
            points: g.type === 'POLYGON' ? g.data?.points : undefined
          })) || [],
          memberCount: selectedLocation.memberCount || 0,
          isAllMembers: !selectedLocation.restrict_to_location_members,
          constraintSets: selectedLocation.constraintSets || [],
          isActive: true,
          locationType: 'physical', // Ensure physical is set for existing locations
          enforceGeofence: selectedLocation.enforceGeofence || false
        } : null}
      />

    </div>
  )
}